import React, { useEffect, useState } from "react";
import { Button, Col, DatePicker, Form, Input, Row, Table, Tooltip } from "antd";
import { FormInstance } from "antd/es/form/Form";
import moment from "moment";
import { Link } from "react-router-dom";
import plusIcon from "../../img/whitePlus.svg";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils";
import { useAppDispatch, useAppSelector } from "../..";
import {
  displayFormat,
  toDisplayFormat,
  toHttpFormat,
  toHttpFormatEndOfDay,
} from "../../lib/utils/formatDate";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import { ReactComponent as CancelButtonIcon } from "../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as SubmitButtonIcon } from "../../img/buttons_icons/submitButton_icon.svg";
import { ReactComponent as CloseVersionIcon } from "../../img/versions_icons/closeVersion_icon.svg";
import { ReactComponent as AddVersionIcon } from "../../img/versions_icons/addVersion_icon.svg";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";
import {
  BlockingTypesAdminTable,
  BlockingTypesAdminVersionsTable,
} from "./BlockingTypesAdminInterface";
import { expandedTableEyeIcon, searchIconForInput } from "../../lib/icons";
import ToggleColumns from "../ToggleableVersionsTable/ToggleColumns";
import {
  deleteBlockingTypes,
  fetchBlockingTypesVersions,
  updateBlockingTypes,
} from "../../store/slices/blocking";
import HistoryAndPropertyModal from "../HistoryAndPropertyModal/HistoryAndPropertyModal";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ADMIN_BLOCKING_TYPE_WRITE } from "../../lib/const/permissions";

interface Props {
  form: FormInstance<any>;
  handleSearch: (values) => void;
}

const columnsWidths = {
  ...versionsTableUtils.columnsWidths,
  alias: "25%",
  priority: "25%",
  name: "25%",
};

const nowDate = toHttpFormat(moment());

const dateFormat = displayFormat;

function BlockingTypesAdmin({ form, handleSearch }: Props) {
  const dispatch = useAppDispatch();

  const [editingKey, setEditingKey] = useState<number | string | null>(null);
  const [formCurrencyEditStatus, setFormCurrencyEditStatus] = useState("idle");
  const isEditing = (rec: BlockingTypesAdminVersionsTable) => rec.key === editingKey;
  const isEditingToDate = formCurrencyEditStatus === "isEditingToDate";
  const isMakingNewVersion = formCurrencyEditStatus === "createNewVersion";

  const {
    blockingTypesLoading,
    blockingTypes,
    blockingTypesVersions,
    blockingTypesVersionsLoading,
  } = useAppSelector((state) => state.blocking);

  const { tenant } = useAppSelector((state) => state.tenants);

  const [dateFilter, setDateFilter] = useState<moment.Moment | null>(moment().startOf("day"));

  const onSearchFilter = () => {
    setEditingKey(null);
    handleSearch({ onDate: dateFilter });
  };

  const [tableData, setTableData] = useState<BlockingTypesAdminTable[]>([]);
  const [tableDataVersions, setTableDataVersions] = useState<BlockingTypesAdminVersionsTable[]>([]);

  const [filterAgreementsIdsForm] = Form.useForm();
  const [activeIds, setActiveIds] = useState<number[]>([]);

  const [selectedVersionsId, setSelectedVersionsId] = useState<number | null>(null);

  useEffect(() => {
    if (checkIsLoaded(blockingTypesLoading)) {
      setTableData(
        blockingTypes
          ?.map((item) => ({
            ...item,
            key: item.id,
            period: item,
          }))
          .filter(({ id }) => {
            if (activeIds.length === 0) {
              return true;
            }
            return activeIds.some((activeId) => String(id).startsWith(String(activeId)));
          })
      );
    }
  }, [blockingTypes, blockingTypesLoading, activeIds]);

  useEffect(() => {
    if (checkIsLoaded(blockingTypesVersionsLoading)) {
      setTableDataVersions(
        blockingTypesVersions
          ?.map((item, index) => ({
            ...item,
            key: `${index}prop`,
            period: item,
            currentDate: moment().isBetween(item.fromDate, item.toDate),
            pastDate: moment(item.fromDate).isBefore(nowDate),
            futureDate: moment(item.fromDate).isAfter(nowDate),
            lastVersion: String(index) === String(0) ? "lastVersion" : "",
          }))
          .sort((a, b) => (moment(b.fromDate).isBefore(moment(a.fromDate)) ? -1 : 1))
      );
    }
  }, [blockingTypesVersions, blockingTypesVersionsLoading]);

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  const showHistoryModal = (rec) => {
    setHistoryModalVisible(true);
    setObjectId(rec.id);
  };

  const renderIdTitle = () => {
    const notValidRgx = /[^0-9,]/;
    let valueTrim = "";
    const onChangeId = ({ target: { value } }) => {
      valueTrim = value.replace(/^\s+/g, "");
      filterAgreementsIdsForm.setFieldsValue({ ids: valueTrim });
      if (!notValidRgx.test(value)) {
        const activeIdes = valueTrim?.split(/\D/).filter(Boolean).map(Number);
        setActiveIds(activeIdes);
      }
    };

    return (
      <Tooltip title="Введите ID типов через ','">
        <Form form={filterAgreementsIdsForm}>
          <Form.Item
            name="ids"
            style={{ marginBottom: "-4px" }}
            className="form-item-required__hidden-error__mb0"
            rules={[
              {
                validator: (x, value) => {
                  if (value && /[^0-9,]/.test(value)) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              aria-autocomplete="none"
              placeholder="ID заявок"
              allowClear
              className="table-head-filter-search"
              suffix={searchIconForInput()}
              onChange={onChangeId}
            />
          </Form.Item>
        </Form>
      </Tooltip>
    );
  };

  renderIdTitle.toString = () => "ID";

  const getColumns = () => [
    {
      title: renderIdTitle,
      dataIndex: "id",
      key: "id",
      width: columnsWidths.id,
      hidden: true,
    },
    {
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      width: columnsWidths.name,
    },
    {
      title: "Приоритет",
      dataIndex: "priority",
      key: "priority",
      width: columnsWidths.priority,
    },
    {
      title: "Alias",
      dataIndex: "alias",
      key: "alias",
      width: columnsWidths.alias,
    },
    {
      title: "Период действия",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      // defaultSortOrder: "descend",
      render: (val, record) => {
        return (
          <>
            {record.fromDate ? toDisplayFormat(record.fromDate) : ""} -{" "}
            {record.toDate ? toDisplayFormat(record.toDate) : ""}
          </>
        );
      },
      sorter: (a, b) => {
        return moment(a.fromDate, "YYYY-MM-DD").unix() - moment(b.toDate, "YYYY-MM-DD").unix();
      },
    },
    {
      title: "",
      dataIndex: "",
      key: "x",
      width: "168px",
    },
    {
      dataIndex: "",
      key: "button",
      width: columnsWidths.buttons,
      render: (record) => (
        <Row justify="end">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              showHistoryModal(record);
            }}
            className="button-icon iconButton"
            style={{ outline: "none" }}
            icon={<DotsIcon />}
          />
        </Row>
      ),
    },
  ];

  const [columns, setColumns] = useState<any[]>(getColumns());

  useEffect(() => {
    setColumns((prev) => {
      const newColumns = getColumns();
      const getNewColumn = ({ key }) => newColumns.find((item) => item.key === key);
      return prev.map((prevColumn) => {
        const newColumn = getNewColumn(prevColumn);
        if (newColumn) {
          return { ...prevColumn, title: newColumn.title, render: newColumn.render };
        }
        return prevColumn;
      });
    });
  }, []);

  const getColumnsVersions = () => [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: columnsWidths.id,
      hidden: true,
    },
    {
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      width: columnsWidths.name,
      render: (val, record) => {
        const editable = isEditing(record);

        return editable && isMakingNewVersion ? (
          <Form.Item
            className="form-item-required__hidden-error__mb0"
            initialValue={val}
            name="name"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>
        ) : (
          <>{val}</>
        );
      },
    },
    {
      title: "Приоритет",
      dataIndex: "priority",
      key: "priority",
      width: columnsWidths.priority,
    },
    {
      title: "Alias",
      dataIndex: "alias",
      key: "alias",
      width: columnsWidths.alias,
      render: (val, record) => {
        const editable = isEditing(record);

        return editable && isMakingNewVersion ? (
          <Form.Item
            className="form-item-required__hidden-error__mb0"
            initialValue={val}
            name="alias"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>
        ) : (
          <>{val}</>
        );
      },
    },
    {
      title: "Период действия",
      dataIndex: "period",
      key: "period",
      // width: "298px",
      width: columnsWidths.period,
      // defaultSortOrder: "descend",

      render: (_, record) => {
        const editable = isEditing(record);

        const disabledDates = (current) =>
          current.isBefore(moment(record.fromDate).add(1, "day")) ||
          current.isAfter(moment(record.toDate));

        return editable ? (
          <>
            {isMakingNewVersion && (
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                <Form.Item
                  name="fromDate"
                  className="form-item-required__hidden-error__mb0"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                  initialValue={moment(record.fromDate).add(1, "day")}
                >
                  <DatePicker
                    format={dateFormat}
                    disabledDate={disabledDates}
                    style={{ width: "115px" }}
                    allowClear={false}
                  />
                </Form.Item>
                <Form.Item className="form-item-required__hidden-error__mb0">
                  - {record.toDate ? toDisplayFormat(record.toDate) : ""}
                </Form.Item>
              </div>
            )}
            {isEditingToDate && (
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                <Form.Item className="form-item-required__hidden-error__mb0">
                  {record.fromDate ? toDisplayFormat(record.fromDate) : ""}-{" "}
                </Form.Item>
                <Form.Item
                  name="toDate"
                  className="form-item-required__hidden-error__mb0"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                  initialValue={moment(record.toDate)}
                >
                  <DatePicker
                    format={dateFormat}
                    disabledDate={disabledDates}
                    style={{ width: "115px" }}
                    allowClear={false}
                  />
                </Form.Item>
              </div>
            )}
          </>
        ) : (
          <>
            {record.fromDate ? toDisplayFormat(record.fromDate) : ""} -{" "}
            {record.toDate ? toDisplayFormat(record.toDate) : ""}
          </>
        );
      },
    },
    {
      title: "",
      dataIndex: "",
      key: "x",
      width: "203px",
      render: (
        val: BlockingTypesAdminVersionsTable,
        record: BlockingTypesAdminVersionsTable,
        index: number
      ) => {
        const editable = isEditing(record);
        const disabled = isMakingNewVersion || isEditingToDate;

        const handleEdit = (currencyEditStatus: string) => () => {
          form.resetFields();
          setEditingKey(() => record.key);
          setFormCurrencyEditStatus(currencyEditStatus);
        };

        const addNewVersion = (currencyEditStatus: string) => () => {
          form.resetFields();

          setEditingKey(() => `new${record.key}`);
          setFormCurrencyEditStatus(currencyEditStatus);

          const newRecord = {
            ...tableDataVersions[index],
            key: `new${tableDataVersions[index].key}`,
          };

          setTableDataVersions((prev) => [
            ...prev.slice(0, index),
            newRecord,
            ...prev.slice(index),
          ]);
        };

        const handleCancel = () => {
          form.resetFields();
          setEditingKey(null);
          if (isMakingNewVersion)
            setTableDataVersions((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);

          setFormCurrencyEditStatus("idle");
        };

        const handleSubmit = () => {
          form.validateFields().then(async () => {
            if (isEditingToDate) {
              dispatch(
                deleteBlockingTypes({
                  blockingTypeId: record.id,
                  fromDate: toHttpFormatEndOfDay(form.getFieldValue("toDate")),
                  onSuccess: () => {
                    form.resetFields();
                    setFormCurrencyEditStatus("idle");
                    onSearchFilter();
                    selectedVersionsId && dispatch(fetchBlockingTypesVersions(selectedVersionsId));
                  },
                })
              );
            }
            if (isMakingNewVersion) {
              const newValues = form.getFieldsValue(["name", "alias", "fromDate"]);

              dispatch(
                updateBlockingTypes({
                  blockingTypeId: record.id,
                  fromDate: toHttpFormat(newValues.fromDate),
                  body: {
                    id: record.id,
                    alias: newValues?.alias,
                    name: newValues?.name,
                    fromDate: toHttpFormat(newValues.fromDate),
                    priority: record.priority,
                    toDate: record.toDate,
                    dsc: record.dsc,
                    tenantId: record.tenantId,
                  },
                  onSuccess: () => {
                    form.resetFields();
                    setFormCurrencyEditStatus("idle");
                    onSearchFilter();
                    selectedVersionsId && dispatch(fetchBlockingTypesVersions(selectedVersionsId));
                  },
                })
              );
            }
          });
        };

        return editable ? (
          <div style={{ display: "flex", justifyContent: "end" }}>
            <Tooltip title="Подтвердить">
              <Button
                className="button-icon"
                icon={<SubmitButtonIcon />}
                type="text"
                htmlType="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleSubmit();
                }}
              />
            </Tooltip>
            <Tooltip title="Отменить">
              <Button
                className="button-icon"
                icon={<CancelButtonIcon />}
                type="text"
                htmlType="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
              />
            </Tooltip>
          </div>
        ) : (
          <>
            {record.tenantId === tenant?.id && record.tenantId !== 0 && (
              <div style={{ display: "flex", justifyContent: "end" }}>
                {hasAccess(MENU_ADMIN_BLOCKING_TYPE_WRITE) && (
                  <Tooltip title="Изменить дату закрытия">
                    {(record.currentDate || record.futureDate) && (
                      <Button
                        className="button-icon"
                        icon={<CloseVersionIcon />}
                        type="text"
                        htmlType="button"
                        disabled={disabled}
                        onClick={handleEdit("isEditingToDate")}
                      />
                    )}
                  </Tooltip>
                )}

                {hasAccess(MENU_ADMIN_BLOCKING_TYPE_WRITE) && (
                  <Tooltip title="Создать версию">
                    {(record.futureDate || record.currentDate) && (
                      <Button
                        className="button-icon"
                        icon={<AddVersionIcon />}
                        type="text"
                        htmlType="button"
                        disabled={disabled}
                        onClick={addNewVersion("createNewVersion")}
                      />
                    )}
                  </Tooltip>
                )}
              </div>
            )}
          </>
        );
      },
    },
    {
      dataIndex: "",
      key: "button",
      width: columnsWidths.buttons,
      render: (record) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            showHistoryModal(record);
          }}
          className="button-icon iconButton"
          style={{ outline: "none" }}
          icon={<DotsIcon />}
        />
      ),
    },
  ];

  const [columnsVersions, setColumnsVersions] = useState<any[]>(getColumnsVersions());

  useEffect(() => {
    setColumnsVersions((prev) => {
      const newColumnsInner = getColumnsVersions();
      const getNewColumn = ({ key }) => newColumnsInner.find((item) => item.key === key);
      return prev.map((prevColumn) => {
        const newColumn = getNewColumn(prevColumn);
        if (newColumn) {
          return { ...prevColumn, title: newColumn.title, render: newColumn.render };
        }
        return prevColumn;
      });
    });
  }, [editingKey, tenant, tableDataVersions, selectedVersionsId]);

  const [
    expandedCurrentStateVersionsTableRows,
    setExpandedCurrentStateVersionsTableRows,
  ] = useState<number[]>([]);

  const buildRowClassName = (record: BlockingTypesAdminVersionsTable): string => {
    const editable = isEditing(record);
    const editNew = String(record.key).includes("new");
    const activeClassName = "currency-version-row";

    if (record.currentDate && !editable) return `${activeClassName} current-date`;
    if (record.currentDate && editable && !editNew)
      return `${activeClassName} current-date active-row`;
    if (record.currentDate && editable && editNew)
      return `${activeClassName} current-date active-row new-prop` || "";
    if (record.pastDate && !editable) return `${activeClassName} past-date`;
    if (record.pastDate && editable) return `${activeClassName} past-date active-row`;
    if (record.futureDate && !editable) return `${activeClassName} future-date`;
    if (record.futureDate && editable) return `${activeClassName} future-date active-row`;
    return "";
  };

  const expandedRowCurrentStateRender = () => (
    <div className="version-inner-table">
      <Form form={form}>
        <Table
          className="inner-table"
          size="small"
          columns={columnsVersions.filter(({ hidden }) => !hidden)}
          dataSource={tableDataVersions}
          pagination={false}
          showHeader={false}
          rowClassName={buildRowClassName}
          loading={checkIsLoading(blockingTypesVersionsLoading)}
        />
      </Form>
    </div>
  );

  return (
    <>
      <div className="inner-row" style={{ marginBottom: "15px" }}>
        <Row align="middle" justify="space-between">
          <h1>Типы блокировок</h1>
          <Col>
            {hasAccess(MENU_ADMIN_BLOCKING_TYPE_WRITE) && (
              <Link to="blocking-types/create">
                <Button
                  type="primary"
                  className="addTariffBtn"
                  icon={<img src={plusIcon} width="8px" alt="plus" />}
                >
                  &nbsp;&nbsp; Добавить новый тип
                </Button>
              </Link>
            )}
          </Col>
        </Row>

        <Row align="middle" justify="space-between">
          <Col span={10}>
            <label style={{ marginRight: "10px" }}>Дата</label>
            <DatePicker
              onChange={(val) => setDateFilter(val)}
              allowClear={false}
              style={{ width: 152 }}
              format={dateFormat}
              value={dateFilter}
            />
            <Button onClick={onSearchFilter} type="primary" style={{ marginLeft: "15px" }}>
              Применить
            </Button>
          </Col>
        </Row>
      </div>
      <Row justify="end" style={{ marginBottom: "-6px" }}>
        <Col style={{ marginBottom: "-32px", zIndex: 1, position: "sticky" }}>
          <ToggleColumns
            tableName="common-profiles-table"
            columns={columns}
            setColumns={setColumns}
            setChildTableColumns={setColumnsVersions}
          />
        </Col>
      </Row>
      <Table
        size="small"
        className="version-table"
        columns={columns.filter(({ hidden }) => !hidden)}
        dataSource={tableData}
        pagination={false}
        loading={checkIsLoading(blockingTypesLoading)}
        expandedRowKeys={expandedCurrentStateVersionsTableRows}
        expandable={{
          expandIconColumnIndex: columns.filter(({ hidden }) => !hidden).length - 1,
          expandedRowRender: expandedRowCurrentStateRender,
          expandIcon: ({ expanded, record }) => {
            const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
              e.stopPropagation();
              setTableDataVersions([]);
              form.resetFields();
              if (!expanded) {
                setExpandedCurrentStateVersionsTableRows([record.key]);
                setFormCurrencyEditStatus("idle");
                setEditingKey("");
                dispatch(fetchBlockingTypesVersions(record.id));
                setSelectedVersionsId(record.id);
              } else {
                setExpandedCurrentStateVersionsTableRows([]);
              }
            };
            return (
              <div className="version-table__eye-wrapper">
                <Button
                  icon={expandedTableEyeIcon(expanded ? "#0E5298" : "#86ACD2")}
                  onClick={handleClick}
                  type="text"
                  htmlType="button"
                  className="iconButton"
                />
              </div>
            );
          },
        }}
      />
      <HistoryAndPropertyModal
        objectTypeSysname="blocking_type"
        objectId={objectId}
        handleHideModal={() => setHistoryModalVisible(false)}
        visibleModal={historyModalVisible}
        hasAccessToEdit={hasAccess(MENU_ADMIN_BLOCKING_TYPE_WRITE)}
      />
    </>
  );
}

export default BlockingTypesAdmin;
